import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { WattsLogo } from "@/components/WattsLogo";

export const Route = createFileRoute("/welcome")({
  component: Landing,
});

function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate({ to: "/map" });
  }, [user, loading, navigate]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-6 py-12"
      style={{ background: "#0f0f0f", fontFamily: "'DM Sans', system-ui, sans-serif" }}
    >
      <div
        className="flex w-full max-w-md flex-1 flex-col items-center justify-center text-center"
        style={{ animation: "wattsFadeIn 0.6s ease forwards", opacity: 0 }}
      >
        <WattsLogo width={200} />

        <h1
          className="mt-10 text-white"
          style={{
            fontFamily: "'Barlow Condensed', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 40,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
          }}
        >
          {t("profile.landing.title")}
        </h1>

        <p className="mt-3" style={{ color: "#888888", fontSize: 16 }}>
          {t("profile.landing.subtitle")}
        </p>

        <div className="mt-10 flex w-full flex-col gap-3">
          <button
            onClick={() => navigate({ to: "/auth" })}
            className="press-scale w-full rounded-full py-4 text-sm font-bold transition-opacity"
            style={{ background: "#F5C518", color: "#000" }}
          >
            {t("profile.landing.cta")}
          </button>

          <button
            onClick={() => navigate({ to: "/auth" })}
            className="press-scale w-full rounded-full border border-[#2a2a2a] bg-[#1a1a1a] py-4 text-sm font-medium text-white transition-colors hover:border-[#F5C518]/40"
          >
            {t("profile.landing.existing")}
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-center gap-5">
        <a href="#" aria-label="X" className="text-[#666] transition-colors hover:text-[#f5c518]">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2H21l-6.52 7.45L22 22h-6.84l-4.78-6.27L4.8 22H2l7-8L2 2h6.91l4.32 5.71L18.244 2zm-2.39 18h1.86L7.27 4H5.29l10.564 16z" />
          </svg>
        </a>
        <a
          href="https://www.instagram.com/watts__app?igsh=MTl1aWJiZ3NxZHBpNA%3D%3D&utm_source=qr"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className="text-[#666] transition-colors hover:text-[#f5c518]"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
          </svg>
        </a>
        <a href="#" aria-label="YouTube" className="text-[#666] transition-colors hover:text-[#f5c518]">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
          </svg>
        </a>
        <a href="#" aria-label="LinkedIn" className="text-[#666] transition-colors hover:text-[#f5c518]">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45z" />
          </svg>
        </a>
        <a href="#" aria-label="TikTok" className="text-[#666] transition-colors hover:text-[#f5c518]">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.93a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
          </svg>
        </a>
      </div>

      <p
        className="text-center"
        style={{ fontFamily: "'DM Sans', system-ui, sans-serif", fontSize: 11, color: "#444444" }}
      >
        {t("profile.landing.copyright")}
      </p>

      <style>{`@keyframes wattsFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
